import React, { useState, useRef, useCallback } from 'react';
import { Button, Modal } from 'lego-on-react';
import block from 'propmods';
import { GrammarIntentShort } from '../../model/grammar';
import { useFormSettings } from '../../hooks/useFormSettings';
import { UnmanagedConfirm, Handles as ConfirmHandles } from '../Confirm/UnmanagedConfirm';
import { CheckboxField } from '../Form/Form';
import { ActionButton } from '../ActionButton/ActionButton';
import { getBaseTooltipReducer } from '../../reducers';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { useSettingsPageContext } from '../../context/settingsPageContext';
import { granetApi } from '../../api';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import * as keyset from 'i18n/IntentsPage';
import i18n from '@yandex-int/i18n';

import './GrammarPageFlagsSettings.scss';

const b = block('GrammarPageFlagsSettings');
const t = i18n(keyset);

interface Props {
    intent: GrammarIntentShort;
    disabled?: boolean;
}

export const GrammarPageFlagsSettings: React.FC<Props> = ({ intent, disabled }) => {
    const { isActivation } = intent;
    const [isSettingsChanged, setIsSettingsChanged] = useState(false);
    const [popupIsVisible, setPopupIsVisible] = useState(false);
    const { settings, onChange } = useFormSettings({
        defaultSettings: { isActivation: isActivation ?? false },
        registerChanges: setIsSettingsChanged,
    });
    const { isDraft } = useSettingsPageContext();
    const skill = useCurrentAliceApp()!;
    const confirmRef = useRef<ConfirmHandles>(null);

    const showPopup = useCallback(() => setPopupIsVisible(true), [setPopupIsVisible]);
    const hidePopup = useCallback(() => setPopupIsVisible(false), [setPopupIsVisible]);

    const onModalClose = useCallback(() => {
        isSettingsChanged
            ? confirmRef.current?.open({
                  title: t('warning-title'),
                  description: t('warning-message'),
                  onConfirm: () => {
                      // init flags values
                      onChange('isActivation')(isActivation);
                      hidePopup();
                  },
              })
            : hidePopup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSettingsChanged, hidePopup, isActivation]);

    const [onSave, state] = useFetchingHandler({
        throwErrors: true,
        handler: async () => {
            await granetApi.patchIntentFlags(intent.id, skill.id, skill.channel, settings, isDraft);
        },
        onLoad: () => setIsSettingsChanged(false),
    });

    const isSaving = state === 'loading';

    return (
        <div {...b()}>
            <Button size="m" theme="normal" onClick={showPopup}>
                {t('parameters')}
            </Button>
            <Modal autoclosable visible={popupIsVisible} directions={['bottom-left']} onOutsideClick={onModalClose}>
                <div {...b('form')}>
                    <h2 {...b('heading')}>{t('parameters')}</h2>
                    <CheckboxField
                        checked={settings.isActivation}
                        onChange={() => onChange('isActivation')(!settings.isActivation)}
                        disabled={disabled}
                    >
                        {t('activation-grammar')}
                    </CheckboxField>
                    <ActionButton
                        tooltipReducer={getBaseTooltipReducer({ successLabel: t('tooltip-saved') })}
                        action={onSave}
                        defaultTooltipState={{ tooltipVisible: false }}
                        disabled={disabled || isSaving}
                    >
                        {t('button-save')}
                    </ActionButton>
                </div>
                <UnmanagedConfirm ref={confirmRef} />
            </Modal>
        </div>
    );
};
